import { useReactFlow, useStoreApi, type XYPosition } from '@xyflow/react';
import { type PointerEvent, useState } from 'react';

/** A pointer that moves less than this, in screen pixels, clicks instead of drags. */
const DRAG_THRESHOLD = 3;

/** A drag in progress: a route handle or the label, with its live result. */
export type LineDrag =
  | { kind: 'route'; waypoints: XYPosition[] }
  | { kind: 'label'; fraction: number };

/**
 * Returns a pointer-down handler that starts a drag. `onMove` returns the live
 * result of each pointer move, in canvas pixels. `onEnd` gets the last result
 * when the user releases the pointer.
 */
export type StartLineDrag = (
  onMove: (point: XYPosition) => LineDrag,
  onEnd: (drag: LineDrag) => void
) => (event: PointerEvent<Element>) => void;

/**
 * Drags a part of a line with the primary button. The drag selects the line
 * if it is not selected, so the user does not have to click it first. The
 * element under the pointer captures the pointer, so it keeps its cursor
 * until the user releases the pointer.
 */
export function useLineDrag({
  edgeID,
  selected,
  disabled,
}: {
  edgeID: string;
  selected: boolean;
  disabled: boolean;
}) {
  const { screenToFlowPosition } = useReactFlow();
  const store = useStoreApi();
  const [drag, setDrag] = useState<LineDrag | null>(null);

  const startDrag: StartLineDrag = (onMove, onEnd) => (event) => {
    if (disabled || event.button !== 0) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    // React Flow selects an edge in the same way when the user clicks it.
    if (!selected) store.getState().addSelectedEdges([edgeID]);

    const start = { x: event.clientX, y: event.clientY };
    let last: LineDrag | null = null;
    const listeners = new AbortController();
    const finish = (save: boolean) => {
      listeners.abort();
      setDrag(null);
      if (save && last) onEnd(last);
    };
    window.addEventListener(
      'pointermove',
      (moveEvent) => {
        const moved = Math.hypot(
          moveEvent.clientX - start.x,
          moveEvent.clientY - start.y
        );
        // A click or double-click selects or edits without moving the line.
        if (!last && moved < DRAG_THRESHOLD) return;
        last = onMove(
          screenToFlowPosition({ x: moveEvent.clientX, y: moveEvent.clientY })
        );
        setDrag(last);
      },
      { signal: listeners.signal }
    );
    window.addEventListener('pointerup', () => finish(true), {
      signal: listeners.signal,
    });
    window.addEventListener('pointercancel', () => finish(false), {
      signal: listeners.signal,
    });
  };

  return { drag, startDrag };
}
